import React, { useEffect, useState } from 'react';
import { translate } from '../../Services/translation';
import OrderListItemMobileOmnium from './ui/OrderListItemMobileOmnium';
import OrderListItemDesktopOmnium from './ui/OrderListItemDesktopOmnium';
import { breakpoint } from '../../styling/mixins/breakpoints';
import styled from 'styled-components';
import { fontFamilyBold, text } from '../../styling/mixins/typography';
import { getColor } from '../../styling/mixins/colors';
import { cells, grid } from '../../styling/utils/grid';
import { hideFor, showFor } from '../../styling/utils/visibility';
import { btn } from '../../styling/mixins/buttons';
import { calculatePager, Pager } from '../../Services/Pagination.service';
import Pagination from '../Pagination';
import { OmniumOrder } from '../../Types/Order';
import SkeletonListItemDesktop from './ui/SkeletonListItemDesktop';
import SkeletonListItemMobile from './ui/SkeletonListItemMobile';
import EditorString from '../EditorString/EditorString';
import { PaginationOptions } from '../../constants';
import {
  downloadPdf,
  loadOmniumOrdersForCustomer,
} from '../../Actions/OmniumOrder.action';

const StyledOrderHistory = styled.div<{ customProp: boolean }>`
  grid-column: span 3;

  .title {
    margin-bottom: 0.8rem;
  }

  .description {
    ${text('large')}
    max-width: 768px;
    margin-bottom: 1.6rem;

    ${breakpoint('large')} {
      margin-bottom: 2.4rem;
    }
  }

  .list-mobile {
    ${hideFor('large')}
  }

  .list-desktop {
    ${showFor('large')}
    border: 1px solid ${getColor('grey', 300)};
    border-radius: 4px;
  }

  .desktop-header {
    display: grid;
    ${(props) => (props.customProp ? cells(10) : cells(7))}
    padding: ${(props) => (props.customProp ? '0.4rem' : '0.8rem')};
    gap: ${(props) => (props.customProp ? '0.8rem' : '1.6rem')};

    border-bottom: 1px solid ${getColor('grey', 300)};
    background-color: ${getColor('white')};

    &__item {
      ${text('large')}
      ${fontFamilyBold}
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  .grid-span-2 {
    grid-column: span 2 / span 2;
  }

  .show-my-orders-btn {
    ${btn('outlined', 'medium')}
    margin-bottom: 1.6rem;
  }

  .pagination-wrapper {
    margin-top: 1.6rem;
  }

  .error {
    ${text('medium')}
    color: ${getColor('alert')};
  }

  .empty {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 3rem;
  }
`;

const pageSize = PaginationOptions.PageSize;
const MyPagesOrderHistoryOmnium = ({ introText, showTrackingNumber }) => {
  const [pageIndex, setPageIndex] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [pager, setPager] = useState<Pager>({});

  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  let numberOfCells = showTrackingNumber ? 8 : 6;

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    load();
  }, [pageIndex]);

  useEffect(() => {
    setPager(calculatePager(totalCount, pageIndex));
  }, [orders, totalCount]);

  const load = () => {
    setIsLoading(true);
    setOrders([]);
    loadOmniumOrdersForCustomer(pageIndex, pageSize)
      .then((res) => {
        setOrders(res.orders);
        setTotalCount(res.totalCount);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onOrderClick = (order: OmniumOrder) => {
    downloadPdf(order);
  };

  const onPageIndexChanged = (index) => {
    if (index != pageIndex) {
      setPageIndex(index);
    }
  };

  return (
    <StyledOrderHistory customProp={showTrackingNumber}>
      <h1 className="title">{translate('mypage.order.title')}</h1>
      <div className="description">
        <EditorString value={introText.value} />
      </div>
      <ul className="list-mobile">
        <SkeletonListItemMobile items={3} cells={5} loading={isLoading} />
        {orders &&
          orders.map((order: OmniumOrder) => (
            <OrderListItemMobileOmnium
              order={order}
              key={order.id + 'mobile'}
              onClick={onOrderClick}
              showTrackingNumber={showTrackingNumber}
            />
          ))}
      </ul>

      <ul className="list-desktop">
        <div className="desktop-header">
          <p className="desktop-header__item">
            {translate('general.ordernumber')}
          </p>
          <p className="desktop-header__item">
            {translate('general.quantity')}
          </p>
          <p
            className={
              showTrackingNumber
                ? 'desktop-header__item grid-span-2'
                : 'desktop-header__item'
            }
          >
            {translate('orderdetail.grandtotal')}
          </p>
          <p className="desktop-header__item">
            {translate('orderdetail.orderdate')}
          </p>
          {showTrackingNumber && (
            <p
              className={
                showTrackingNumber
                  ? 'desktop-header__item grid-span-2'
                  : 'desktop-header__item'
              }
            >
              {translate('orderdetail.trackingnumber')}
            </p>
          )}
          <p className="desktop-header__item">
            {translate('orderdetail.orderstatus')}
          </p>
        </div>
        <SkeletonListItemDesktop
          items={5}
          cells={numberOfCells}
          loading={isLoading}
        />
        {orders &&
          orders.map((order: OmniumOrder) => (
            <OrderListItemDesktopOmnium
              order={order}
              key={order.id + 'desktop'}
              onClick={onOrderClick}
              showTrackingNumber={showTrackingNumber}
            />
          ))}
      </ul>
      {!isLoading && (!orders || orders.length == 0) && (
        <div className="empty">
          <p>{translate('mypage.order.no-orders')}</p>
        </div>
      )}
      <div className="pagination-wrapper">
        <Pagination
          model={pager}
          onChange={(index) => onPageIndexChanged(index)}
        />
      </div>
    </StyledOrderHistory>
  );
};

export default MyPagesOrderHistoryOmnium;
